import { BaseTextField } from '@components/BaseTextField';
import StyledDateRangePicker from '@components/date-range-select/StyledDateRangePicker';
import FilterRoot from '@components/filter/FilterRoot';
import StyledAutocomplete from '@components/styled-autocomplete';
import useStyledAutocomplete from '@components/styled-autocomplete/useStyledAutocomplete';
import { BaseHead, CellOfRow } from '@components/tables';
import {
    Box,
    Pagination as MuiPagination,
    Stack,
    Table,
    TableBody,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import { useCameraDetailContext } from '@pages/cameras/hook/CameraDetailsProvider';
import ListNoData from '@pages/cameras/list/components/NoDataCameras';
import { search } from '@pages/cameras/list/helpers';
import SkeletonEventLogTable from '@pages/verification/pc-details/components/recordedPCNEventLog/components/SkeletonEventLogTable';
import { formatEnumValue } from '@pages/verification/pc-details/components/recordedPCNEventLog/helpers';
import { FiSearch } from 'react-icons/fi';
import { formatUKDateTime } from 'src/helpers';
import { entityNameOptions, eventLogType, typeEventLog } from './configs';
import { AuditTableCell, calculateChanges } from './helpers';
import { CameraTrailAuditEvent, CameraTrailAuditEventEntityName, CameraTrailAuditEventType } from '@EcamModel/model/CameraTrailAudit';
import { CameraType } from '@EcamModel/controllers/ICameraHttpController';
import { typeTextMap } from '@pages/cameras/hook/GeoZonesNavigateHandler';
import useMedia from '@hooks/useMedia';
import { useMemo } from 'react';

export default function RecordedEventLog() {
    const geoZonesContext = useCameraDetailContext();
    const { isMediumMD, isMobileSM } = useMedia()

    const {
        camera,
        isFetching,
        isLoadingTrailAuditEvent,
        filterTrailAuditEvent,
        filterSortStateTrailAuditEvent,
        trailAuditEvent,
        listCameraView,
        setFilterTrailAuditEvent,
        setPartialTrailAuditEvent,
        setFilterSortStateTrailAuditEvent,
    } = geoZonesContext;

    const allGeoZones = useMemo(
        () => listCameraView?.rows?.flatMap((view) => view.GeoZones ?? []) ?? [],
        [listCameraView]
    );

    const typeEventLogComplete = useStyledAutocomplete({
        list: {
            options: typeEventLog,
            isFiltered: (option, searchText) => search(option.Name, searchText),
        },
    });

    const entityNameComplete = useStyledAutocomplete({
        list: {
            options: entityNameOptions,
            isFiltered: (option, searchText) => search(option.Name, searchText),
        },
    });

    const handleClickNavigateTaskScreen = (item: CameraTrailAuditEvent) => {
        const taskName = item.CameraTask?.TaskName;
        const taskId = item.CameraTask?.Id;

        if (taskId) {
            const taskScreenUrl = `/task-management?taskName=${encodeURIComponent(taskName ?? "")}&taskId=${taskId}`;
            window.open(taskScreenUrl, '_blank');
        }
    };

    const handleClickNavigateGeoZone = (item: CameraTrailAuditEvent) => {
        if (!item.Key || item.AuditEventType === CameraTrailAuditEventType.DELETE) return;

        const cameraType = camera.PTZCameraId !== null
            ? CameraType.PTZ
            : camera.SP111CameraId !== null
                ? CameraType.SP111
                : CameraType.Solar;

        window.open(`/cameras/${item.CameraId}/edit/${item.Key}/${typeTextMap[cameraType]}`, '_blank');
    };

    return (
        <Stack padding={2} borderRadius={'0px 16px 16px 16px'} border={'1px solid #E4E7EC'}>
            <Stack mb={2}>
                <FilterRoot
                    handleResetFilter={() => {
                        setFilterTrailAuditEvent({
                            page: 1,
                            from: undefined,
                            to: undefined,
                            search: '',
                            eventLog: undefined,
                            entityName: undefined,
                        });
                    }}
                    filterChild={
                        <Grid2 container xs={12} gap={2} wrap={isMediumMD ? 'wrap' : 'nowrap'}>
                            <Grid2 xs={12} md={3}>
                                <BaseTextField
                                    placeholder={'Search name created by'}
                                    value={filterTrailAuditEvent.search}
                                    onChange={(e) => {
                                        setPartialTrailAuditEvent({
                                            search: e.target.value,
                                            from: filterTrailAuditEvent.from,
                                            to: filterTrailAuditEvent.to,
                                        });
                                    }}
                                    maxLength={50}
                                    InputProps={{
                                        endAdornment: (
                                            <FiSearch
                                                color="disabled"
                                                size={16}
                                                style={{ color: 'rgba(0, 0, 0, 0.38)', cursor: 'pointer' }}
                                            />
                                        ),
                                    }}
                                />
                            </Grid2>
                            <Grid2 xs={12} md={3}>
                                <StyledAutocomplete
                                    {...typeEventLogComplete}
                                    getOptionLabel={(o) => o.Name}
                                    label="Types"
                                    value={filterTrailAuditEvent.eventLog}
                                    placeholder="All types"
                                    onChange={(o) => {
                                        setPartialTrailAuditEvent({
                                            eventLog: o,
                                        });
                                    }}
                                />
                            </Grid2>
                            <Grid2 xs={12} md={3}>
                                <StyledAutocomplete
                                    {...entityNameComplete}
                                    getOptionLabel={(o) => o.Name}
                                    label="Entity"
                                    value={filterTrailAuditEvent.entityName}
                                    placeholder="All entities"
                                    onChange={(o) => {
                                        setPartialTrailAuditEvent({
                                            entityName: o,
                                        });
                                    }}
                                />
                            </Grid2>
                            <Grid2 xs={12} md={3}>
                                <StyledDateRangePicker
                                    value={{
                                        startDate: filterTrailAuditEvent?.from || undefined,
                                        endDate: filterTrailAuditEvent?.to || undefined,
                                    }}
                                    onChange={(value) => {
                                        setPartialTrailAuditEvent({
                                            from: value.startDate,
                                            to: value.endDate,
                                            search: filterTrailAuditEvent.search,
                                        });
                                    }}
                                    dateReset={{ startDate: undefined, endDate: undefined }}
                                    label={'Select date'}
                                />
                            </Grid2>
                        </Grid2>
                    }
                />
            </Stack>
            {isLoadingTrailAuditEvent || isFetching ? (
                <SkeletonEventLogTable
                    rows={7}
                    columns={4}
                    skeletonWidths={[100, 100, 100, 300]}
                    nestedSkeletonWidths={[400, 400]}
                />
            ) : (
                <TableContainer component={Box}>
                    {trailAuditEvent?.rows?.length! > 0 ? (
                        <Table aria-label="simple table">
                            <TableHead>
                                <TableRow>
                                    <BaseHead
                                        align="left"
                                        title="Date/time"
                                        isLeftLabel
                                        sx={{
                                            justifyContent: 'flex-start',
                                            alignItems: 'start',
                                        }}
                                        sort={
                                            filterSortStateTrailAuditEvent.sorts?.includes('-Updated')
                                                ? 'DSC'
                                                : filterSortStateTrailAuditEvent.sorts?.includes('Updated')
                                                    ? 'ASC'
                                                    : undefined
                                        }
                                        onChangeSort={(s) => {
                                            setFilterSortStateTrailAuditEvent((prev) => ({
                                                ...prev,
                                                sorts: [s === 'ASC' ? 'Updated' : '-Updated'],
                                            }));
                                        }}
                                    />
                                    <BaseHead align="left" title="User" />
                                    <BaseHead align="left" title="Type" />
                                    <BaseHead align="left" title="Details" />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {trailAuditEvent?.rows?.map((item, index) => {
                                    return (
                                        <TableRow key={item.Id}>
                                            <CellOfRow value={formatUKDateTime(item.Created)} align="left" />
                                            <CellOfRow value={item.UpdatedBy ?? ''} align="left" />
                                            <CellOfRow
                                                value={`${formatEnumValue({
                                                    enumKey: item.AuditEventType,
                                                    enumMapping: eventLogType,
                                                    capitalizeFirstLetter: true,
                                                })}`}
                                                align="left"
                                            />
                                            <AuditTableCell
                                                item={item}
                                                changes={calculateChanges(item.OldValues, item.NewValues)}
                                                align="left"
                                                entityName={item.EntityName}
                                                handleClickNavigateTaskScreen={handleClickNavigateTaskScreen}
                                                handleClickNavigateGeoZone={handleClickNavigateGeoZone}
                                                allGeoZones={allGeoZones}
                                            />
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    ) : (
                        <ListNoData />
                    )}
                    {trailAuditEvent?.total! > trailAuditEvent?.pageSize! &&
                        !(isLoadingTrailAuditEvent || isFetching) && (
                            <Stack mt={3} direction="row" justifyContent="center">
                                <MuiPagination
                                    count={trailAuditEvent?.totalPages || 1}
                                    page={trailAuditEvent?.page}
                                    onChange={(_, page) =>
                                        setFilterTrailAuditEvent((prev) => ({ ...prev, page: page }))
                                    }
                                    variant="outlined"
                                    shape="rounded"
                                />
                            </Stack>
                        )}
                </TableContainer>
            )}
        </Stack>
    );
}
